/**
 * NPC personas — people as pure functions of the settlement seed.
 *
 * Like towns and resources, NPCs are DERIVED, never stored: every client and
 * the server regenerate the identical roster from a town's seed, so "the
 * gruff warden by the north gate" is the same person for every player,
 * forever, with zero data transfer. Only live state (positions while a room
 * is hot, later: quest/relationship state) exists at runtime.
 *
 * Three sources behind one shape (civilization-roadmap C2):
 *  - "town"  — generated rosters, rolled here from the town seed.
 *  - "canon" — hand-authored story characters (C5; registry stub below).
 *  - "crew"  — the player's recruited companions (C5; persisted per-user).
 */
import { rngFromString, range, pick, type Rng } from "./rng.js";
import * as pmath from "./pmath.js";
import { EYE_COLORS, HAIR_COLORS, SKIN_COLORS, type CharacterStyle } from "./rpg.js";
import { townLayout, townRadiusAt, type BuildingDef, type TownDef } from "./towns.js";
import { personGender, personName } from "./worldgen/names.js";
import { generateNpcPersonality, type NpcPersonality } from "./social/personality.js";

export type NpcRole =
  | "keeper" // hall/chapel — the settlement's voice (quest anchor later)
  | "trader" // market/plaza — barks wares, will run shops (C3+)
  | "warden" // gates/walls — watches the road
  | "farmer" // the working belt
  | "artisan" // the fabric — workshops, yards
  | "wanderer" // roams the whole settlement
  | "elder"; // sits by the well and remembers things

/** What a persona DOES at work — a real place + a motion pattern, not a
 *  hash point (the old anchors read as "standing nowhere for no reason").
 *  "none" = no job: they wander. */
export type NpcJobKind =
  | "stall" // trader: mans a market stall
  | "field" // farmer: paces + tends a crop strip (jobData = work line A→B)
  | "patrol" // warden: walks the road leg to the gate (jobData = A→B)
  | "shopfront" // artisan: works at a workshop door (jobData = face target)
  | "hall" // keeper: holds the hall steps (jobData = face target)
  | "well" // elder: keeps the well company (jobData = face target)
  | "none"; // wanderer: no job — roams

export interface NpcDef {
  /** `${town.seed}:npc:${i}` — stable identity forever. */
  id: string;
  name: string;
  role: NpcRole;
  style: CharacterStyle;
  /** Legacy labels retained for affinity and voice compatibility. */
  traits: [string, string];
  /** Generated baseline. Optional for older/custom persona adapters. */
  personality?: NpcPersonality;
  /** WORK anchor point, town-local meters (the same frame as the layout). */
  u: number;
  v: number;
  /** Wander radius around the anchor (m); small = stands at post. */
  roam: number;
  /** Home building index into townLayout(town).buildings (−1 = none). */
  home: number;
  /** The job worked during "work" slots (see NpcJobKind). */
  job: NpcJobKind;
  /** Job geometry: field/patrol = the work line [ax, az, bx, bz]; the
   *  stand-at jobs = the FACING target in [0..1]; unused = zeros. */
  jobData: [number, number, number, number];
  source: "town" | "region" | "canon" | "crew";
  /** W4 grown-settlement home parcel. Present only when source === "region". */
  regionHomeParcel?: number | null;
  /** W4 grown-settlement workplace parcel. Present only when source === "region". */
  regionWorkplaceParcel?: number | null;
  /** W4 collapsed-history lines, derived locally from the resident's provenance.
   *  Never sent over the wire or persisted. */
  history?: string[];
}

export const NPC_TRAITS = [
  "gruff",
  "cheerful",
  "wary",
  "curious",
  "weary",
  "proud",
  "kind",
  "sly",
  "devout",
  "restless",
] as const;

/** Tiny HSL→RGB (0xRRGGBB) — shared has no THREE. */
function hsl(h: number, s: number, l: number): number {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number): number => {
    const k = (n + h * 12) % 12;
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return (Math.round(f(0) * 255) << 16) | (Math.round(f(8) * 255) << 8) | Math.round(f(4) * 255);
}

/** Role-flavored five-slot outfits; palette rolls retain their original seed order. */
function rollStyle(rng: Rng, role: NpcRole, warm: number, gender: "f" | "m" = "m"): CharacterStyle {
  const d = (p: number): string => (rng() < p ? "drifter" : "none");
  const head = d(0.2), shoulder = d(0.1), hands = d(0.3);
  d(0.15); d(0.4); // former separate pack/belt rolls; now bundled with torso/legs
  const base: CharacterStyle = {
    body: gender === "f" ? "female" : "male", head,
    torso: "drifter", arms: shoulder === "drifter" ? shoulder : hands,
    legs: "drifter", feet: "drifter",
    suit: hsl(warm ? range(rng, 0.04, 0.12) : range(rng, 0.55, 0.65), range(rng, 0.12, 0.3), range(rng, 0.3, 0.55)),
    accent: hsl(range(rng, 0, 1), range(rng, 0.4, 0.7), range(rng, 0.45, 0.6)),
    skin: SKIN_COLORS[Math.floor(rng() * SKIN_COLORS.length)],
    hairColor: HAIR_COLORS[Math.floor(rng() * HAIR_COLORS.length)],
    eyeColor: EYE_COLORS[Math.floor(rng() * EYE_COLORS.length)],
  };
  switch (role) {
    case "warden": base.head = d(0.6); base.arms = "drifter"; break;
    case "trader": d(0.5); break;
    case "elder": base.head = "none"; break;
    case "keeper": base.arms = d(0.5); break;
    case "farmer": base.head = d(0.4); base.arms = "drifter"; break;
    case "artisan": base.arms = "drifter"; break;
  }
  return base;
}

/**
 * How many people live/work in a building — POPULATION SCALES WITH THE
 * SETTLEMENT'S BUILT STOCK (user spec: ~1 per small building, 2 per
 * medium, 4 per large; tall landmarks a little more). Fortifications and
 * outbuildings house no one.
 */
export function npcCapacity(b: BuildingDef): number {
  if (b.kind === "wall" || b.kind === "barn") return 0;
  if (b.kind === "windmill") return 1; // the miller
  if (b.kind === "gate") return 1; // the gate watch
  if (b.kind === "spire") return 1;
  if (b.kind === "dome") return 2;
  const area = b.w * b.d;
  if (area < 90) return 1;
  if (area < 190) return 2;
  // Large footprint; tall large buildings (towers, big civic) hold a bit more.
  // Trimmed 4/6 → 2/3 (2026-07-31 user call: ~1.5 residents per house).
  return b.h > 16 ? 3 : 2;
}

const ROSTERS = new Map<string, NpcDef[]>();

/**
 * The people of a settlement — deterministic, cached by town seed.
 * One roster entry per unit of building capacity: everyone LIVES somewhere
 * (`home` = a layout building index), and their role + work anchor derive
 * from that home (gate towers house wardens, markets house traders, edge
 * houses lean farmer, the fabric leans artisan). The lit-window occupancy
 * and the schedule system read the same home, so a house glows exactly
 * when ITS residents are in.
 */
export function townNpcs(town: TownDef): NpcDef[] {
  const cached = ROSTERS.get(town.seed);
  if (cached) return cached;
  const layout = townLayout(town);
  const warm = town.biome.temp > 0 ? 1 : 0;
  const out: NpcDef[] = [];

  let keeperDone = false;
  let elders = 0;
  const elderCap = town.tier === "city" ? 3 : town.tier === "town" ? 2 : 1;

  // Tier-1 job places — REAL layout features, precomputed once. Anchoring
  // work to hash points made destinations read as random (user-reported).
  const stalls = layout.props.filter((p) => p.kind === "stall");
  const wells = layout.props.filter((p) => p.kind === "well");
  const cropPatches = layout.patches.filter((p) => p.tint <= 2 && Math.max(p.w, p.d) >= 6);
  const hall = layout.buildings.find((b) => b.kind === "hall") ?? null;
  const shopKinds = new Set(["warehouse", "barn", "civic", "tower"]);
  const shops = layout.buildings.filter((b) => shopKinds.has(b.kind));
  const doorOf = (b: BuildingDef): [number, number] => [
    b.u - pmath.sin(b.yaw) * (b.d / 2 + 1.2),
    b.v + pmath.cos(b.yaw) * (b.d / 2 + 1.2),
  ];

  for (let bi = 0; bi < layout.buildings.length; bi++) {
    const b = layout.buildings[bi];
    const cap = npcCapacity(b);
    for (let k = 0; k < cap; k++) {
      const i = out.length;
      const id = `${town.seed}:npc:${i}`;
      const prng = rngFromString(id);

      // Role from the home building (first hall resident is THE keeper).
      let role: NpcRole;
      if ((b.kind === "hall" || b.kind === "civic" || b.kind === "spire" || b.kind === "dome") && !keeperDone) {
        role = "keeper";
        keeperDone = true;
      } else if (b.kind === "market") {
        role = "trader";
      } else if (b.kind === "gate") {
        role = "warden";
      } else if (b.kind === "windmill") {
        role = "farmer";
      } else if (b.kind === "warehouse" || b.kind === "tower") {
        role = prng() < 0.4 ? "trader" : "artisan";
      } else if (b.kind === "hall" || b.kind === "civic" || b.kind === "spire" || b.kind === "dome") {
        role = "artisan";
      } else {
        // Dwellings: edge houses lean farmer, the fabric leans artisan,
        // with wanderers sprinkled through and a few elders near the core.
        const dist = pmath.hypot(b.u, b.v);
        const edge = townRadiusAt(town, pmath.atan2(b.v, b.u));
        const r = prng();
        if (elders < elderCap && dist < edge * 0.45 && r < 0.05 && town.tier !== "outpost") {
          role = "elder";
          elders++;
        } else if (dist > edge * 0.72) {
          role = r < 0.72 ? "farmer" : r < 0.9 ? "artisan" : "wanderer";
        } else {
          role = r < 0.62 ? "artisan" : r < 0.82 ? "farmer" : "wanderer";
        }
      }

      // WORK anchor + JOB from real layout features (Tier 1): the trader
      // has a stall, the farmer a strip of crops, the warden a road to
      // watch — destinations you can SEE the reason for.
      const doorU = b.u - pmath.sin(b.yaw) * (b.d / 2 + 1.1);
      const doorV = b.v + pmath.cos(b.yaw) * (b.d / 2 + 1.1);
      let u = doorU;
      let v = doorV;
      let roam = 6;
      let job: NpcJobKind = "none";
      let jobData: [number, number, number, number] = [0, 0, 0, 0];
      if (role === "keeper") {
        job = "hall";
        const [hu, hv] = hall ? doorOf(hall) : [layout.plazaU, layout.plazaV];
        u = hu + range(prng, -1.5, 1.5);
        v = hv + range(prng, -1.5, 1.5);
        jobData = [layout.plazaU, layout.plazaV, 0, 0]; // face the square
        roam = 4;
      } else if (role === "elder") {
        job = "well";
        const wp = wells.length > 0 ? wells[Math.floor(prng() * wells.length)] : null;
        const cx = wp ? wp.u : layout.plazaU;
        const cv = wp ? wp.v : layout.plazaV;
        const a = prng() * Math.PI * 2;
        u = cx + pmath.cos(a) * 1.8;
        v = cv + pmath.sin(a) * 1.8;
        jobData = [cx, cv, 0, 0]; // face the well
        roam = 2.5;
      } else if (role === "trader") {
        if (stalls.length > 0) {
          job = "stall";
          const st = stalls[Math.floor(prng() * stalls.length)];
          // Stand BEHIND the stall (the side away from the plaza).
          const dx = st.u - layout.plazaU;
          const dv = st.v - layout.plazaV;
          const dl = pmath.hypot(dx, dv) || 1;
          u = st.u + (dx / dl) * 1.5;
          v = st.v + (dv / dl) * 1.5;
          jobData = [st.u, st.v, 0, 0]; // face the stall/customers
          roam = 2.5;
        } else {
          job = "shopfront";
          jobData = [b.u, b.v, 0, 0];
          roam = 3;
        }
      } else if (role === "warden") {
        job = "patrol";
        const ga = town.fingers.length > 0 ? town.fingers[Math.floor(prng() * town.fingers.length)].a : prng() * Math.PI * 2;
        const edge = townRadiusAt(town, ga);
        // Pace the exit road: mid-town out to just inside the gate.
        const ax = pmath.cos(ga) * edge * 0.45;
        const az = pmath.sin(ga) * edge * 0.45;
        const bx = pmath.cos(ga) * edge * 0.88;
        const bz = pmath.sin(ga) * edge * 0.88;
        u = ax;
        v = az;
        jobData = [ax, az, bx, bz];
        roam = 3;
      } else if (role === "farmer") {
        // A REAL crop strip: best of 4 hash picks by distance from home.
        let patch = null as (typeof cropPatches)[number] | null;
        let bd2 = Infinity;
        for (let k2 = 0; k2 < 4 && cropPatches.length > 0; k2++) {
          const cand = cropPatches[Math.floor(prng() * cropPatches.length)];
          const du = cand.u - b.u;
          const dv = cand.v - b.v;
          const d2 = du * du + dv * dv;
          if (d2 < bd2) {
            bd2 = d2;
            patch = cand;
          }
        }
        if (patch) {
          job = "field";
          // Work line = a workable stretch of the strip's LONG axis (the
          // belt furlongs run 100 m+ — pacing the whole thing reads as a
          // route march, and the tending dwells almost never come up).
          const long = patch.w >= patch.d;
          const half = Math.min(14, (long ? patch.w : patch.d) * 0.5 * 0.72);
          const axx = long ? pmath.cos(patch.yaw) : -pmath.sin(patch.yaw);
          const axz = long ? pmath.sin(patch.yaw) : pmath.cos(patch.yaw);
          const ax = patch.u - axx * half;
          const az = patch.v - axz * half;
          jobData = [ax, az, patch.u + axx * half, patch.v + axz * half];
          u = ax; // walk-in lands at the line start (continuity)
          v = az;
          roam = 4;
        } else {
          const a = pmath.atan2(b.v, b.u) + range(prng, -0.35, 0.35);
          const rr = townRadiusAt(town, a) + Math.max(10, town.farmW * range(prng, 0.15, 0.6));
          u = pmath.cos(a) * rr;
          v = pmath.sin(a) * rr;
          roam = 18;
        }
      } else if (role === "artisan") {
        // The nearest workshop-ish building — else the bench at home.
        job = "shopfront";
        let shop: BuildingDef | null = null;
        let sd2 = Infinity;
        for (let k2 = 0; k2 < 6 && shops.length > 0; k2++) {
          const cand = shops[Math.floor(prng() * shops.length)];
          const du = cand.u - b.u;
          const dv = cand.v - b.v;
          const d2 = du * du + dv * dv;
          if (d2 < sd2) {
            sd2 = d2;
            shop = cand;
          }
        }
        const target = shop && sd2 < 150 * 150 ? shop : b;
        const [su, sv] = doorOf(target);
        u = su + range(prng, -1, 1);
        v = sv + range(prng, -1, 1);
        jobData = [target.u, target.v, 0, 0]; // face the bench
        roam = 3;
      } else if (role === "wanderer") {
        job = "none";
        roam = Math.min(60, town.padR * 0.35);
      }

      const t1 = pick(prng, NPC_TRAITS);
      let t2 = pick(prng, NPC_TRAITS);
      if (t2 === t1) t2 = NPC_TRAITS[(NPC_TRAITS.indexOf(t1) + 3) % NPC_TRAITS.length];
      // Wayfarer NPCs (2026-07-31): gender derives from the id — name and
      // body always agree, no stored state.
      const gender = personGender(id);
      out.push({
        id,
        name: personName(id, `${town.seed}:household:${bi}`, town.seed, gender),
        role,
        style: rollStyle(prng, role, warm, gender),
        traits: [t1, t2],
        personality: generateNpcPersonality({ identity: ["town", id], traits: [t1, t2] }),
        u,
        v,
        roam,
        home: bi,
        job,
        jobData,
        source: "town",
      });
    }
  }

  ROSTERS.set(town.seed, out);
  if (ROSTERS.size > 96) {
    const first = ROSTERS.keys().next().value;
    if (first !== undefined) ROSTERS.delete(first);
  }
  return out;
}

/**
 * Placeholder dialog — a greeting shaped by role + trait. C3 replaces this
 * with authored dialog trees; the SHAPE (server-picked line id, client
 * renders) is what matters now.
 */
const GREETINGS: Record<NpcRole, string[]> = {
  keeper: [
    "Welcome to {town}, traveler. Mind the {trait} types around here.",
    "I keep things running in {town}. Barely, some days.",
    "New face. We don't get many since the lanes went quiet.",
  ],
  trader: [
    "Looking to trade? Soon, friend — the manifests are a mess.",
    "Best goods this side of the belt. Or they will be, once the caravan lands.",
    "Coin or ore, I'll deal in either. Come back at market hour.",
  ],
  warden: [
    "Keep your blade stowed inside the walls and we'll get along.",
    "Watched the road three seasons. You're the first interesting thing on it.",
    "Gate's open. Trouble stays outside — that's the arrangement.",
  ],
  farmer: [
    "The selions won't tend themselves. Something you need?",
    "Good soil this season. The windmill's been singing.",
    "You from the port? Tell them the grain tithe's ready.",
  ],
  artisan: [
    "Careful in the workshop rows — sparks travel.",
    "I could do fine work with off-world alloy, if you ever haul any.",
    "Built half this street with these hands. The better half.",
  ],
  wanderer: [
    "I've walked every lane in {town}. Ask me anything. Well — almost.",
    "The roads out there go further than the maps admit.",
    "Stay a night. The lamps here are kinder than the stars.",
  ],
  elder: [
    "Sit a moment. The well's older than the wall, you know.",
    "I remember when this was four houses and a mule.",
    "{trait}, they call me. I call it experienced.",
  ],
};

/** A deterministic greeting line for a persona (varied by a salt). */
export function npcGreeting(npc: NpcDef, townName: string, salt: number): string {
  const lines = GREETINGS[npc.role];
  const line = lines[Math.abs(salt) % lines.length];
  return line.replace("{town}", townName).replace("{trait}", npc.traits[0]);
}

// ---------------------------------------------------------------------------
// Derived stats — identity constants from the seed, zero storage
// ---------------------------------------------------------------------------

/** Warmth multiplies positive relationship deltas (trait-derived). */
export function npcWarmth(npc: NpcDef): number {
  const t = npc.traits;
  if (t.includes("cheerful") || t.includes("kind")) return 1.25;
  if (t.includes("gruff") || t.includes("wary") || t.includes("sly")) return 0.75;
  return 1;
}

/**
 * Mood — a deterministic daily blend every client computes identically
 * (renders as dialog tone; later posture). 0 = sour, 1 = bright.
 */
export function npcMood(npc: NpcDef, dayIndex: number): number {
  let h = 2166136261;
  const s = npc.id + ":" + dayIndex;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const base = ((h >>> 0) % 1000) / 1000;
  const lean = npc.traits.includes("cheerful") ? 0.2 : npc.traits.includes("weary") ? -0.2 : 0;
  return Math.min(1, Math.max(0, base * 0.8 + 0.1 + lean));
}

// ---------------------------------------------------------------------------
// Relationship tiers + tiered dialog pools (state + salt on the wire,
// NEVER text — both sides derive the same line)
// ---------------------------------------------------------------------------

export const NPC_TIERS = ["stranger", "acquaintance", "familiar", "friend", "confidant"] as const;

/** Affinity → tier index (thresholds 10/25/50/80). */
export function npcTier(affinity: number): number {
  return affinity >= 80 ? 4 : affinity >= 50 ? 3 : affinity >= 25 ? 2 : affinity >= 10 ? 1 : 0;
}

const TIER_LINES: string[][] = [
  [
    // stranger — role voice via npcGreeting; these are the generic extras
    "Don't get many new faces out here.",
    "You'll want the {town} hall if you're lost.",
    "Mind the fields on your way out.",
  ],
  [
    "Back again? {town} grows on people.",
    "I remember you. The {trait} one.",
    "Good to see a familiar coat on the lane.",
  ],
  [
    "You should hear what the keeper says about the old wall. Ask sometime.",
    "The mill's been loud all week — something's off with the wind.",
    "If you're trading, morning's best. Everyone's at their posts.",
  ],
  [
    "Always glad when you come through, friend.",
    "I put a little something aside for you — next time, remind me.",
    "The town feels safer with you walking it.",
  ],
  [
    "Between us — {town} wouldn't be standing without people like you.",
    "Whatever the road brings, you've a roof here.",
    "I've told you things I've told no one. Don't make me regret it.",
  ],
];

/**
 * A dialog line for a persona at a relationship tier. Deterministic from
 * (npc, tier, salt) — the server sends only {tier, salt}.
 */
export function npcLine(npc: NpcDef, townName: string, tier: number, salt: number): string {
  const t = Math.max(0, Math.min(NPC_TIERS.length - 1, tier));
  // Familiar residents sometimes surface a fact that actually shaped their
  // persona. The fact is regenerated from W3+W4 data on every peer; the server
  // still sends only relationship state + salt.
  if (t >= 2 && npc.history && npc.history.length > 0 && Math.abs(salt) % 3 === 0) {
    return npc.history[Math.abs(salt >> 2) % npc.history.length];
  }
  // Strangers mostly get the role greeting; higher tiers mix pools.
  const pools = t === 0 ? [GREETINGS[npc.role], TIER_LINES[0]] : [TIER_LINES[t], GREETINGS[npc.role]];
  const pool = pools[Math.abs(salt >> 3) % pools.length];
  const line = pool[Math.abs(salt) % pool.length];
  return line.replace("{town}", townName).replace("{trait}", npc.traits[0]);
}
